// src/components/board/ExportChannelStats.tsx
import React, { useState } from "react";
import {
    Avatar,
    Badge,
    Button,
    Empty,
    Input, List,
    Typography,
    Popover,
    Result,
    Space,
    Spin,
    Tooltip,
    notification,
    Dropdown,
    MenuProps,
    Progress,
} from "antd";
import {
    QuestionCircleOutlined,
    UpOutlined,
    DownOutlined,
    EditOutlined,
    CopyOutlined,
    CheckOutlined,
    CloseOutlined
} from "@ant-design/icons";

import { useTranslation } from "react-i18next";

// @ts-ignore
import UsersManualExport from '@documents/UsersManual_ExportChannelStats.pdf';
import { ChannelDao } from "@repo/dao/ChannelDao";
import { IChannel } from "@repo/types/IChannel";
import uniqid from 'uniqid';
import BaseGrid from "../reactgrid/BaseGrid";
import { detectPublicLink } from "@/services/platform-detector.service";

const { Paragraph, Text } = Typography;

enum Status {
    isBasic,
    isEmpty,
    isLoading,
    isContent,
    isError
}

const UsersManualExportButton: React.FC<any> = () => {
    const { t } = useTranslation();
    return (
        <div style={{ textAlign: "left", width: '100%' }}>
            <Text color="#d9d9d9">{t(`ExportChannelStats.search.pleaseRead`)}
                <a href={UsersManualExport} rel="noopener noreferrer" target="_blank">
                    <Button
                        type="link"
                        style={{ paddingLeft: 4, paddingRight: 0, color: "#9261f1" }}>
                        {t(`ExportChannelStats.search.manual`)}
                    </Button>
                </a>
            </Text>
        </div>
    )
}

const SpinLoading: React.FC<{ progress: number }> = ({ progress }) => (
    <Spin
        tip="Loading"
        size="large"
        style={{ marginTop: -40, marginBottom: 0 }}
    >
        <div style={{
            padding: 50,
            marginTop: 80,
            marginBottom: 80,
            background: 'rgba(0, 0, 0, 0)',
            borderRadius: 4,
        }} />
        <Progress percent={progress} />
    </Spin>
)

const ExportChannelStats: React.FC<any> = () => {
    const [progress, setProgress] = useState<number>(0);
    const columns: Array<any> = [
        { title: 'Статус', dataIndex: 'status', key: 'status', width: 80 },
        { title: 'Платформа', dataIndex: 'platform', key: 'platform', width: 80 },
        { title: 'Площадка', dataIndex: 'title', key: 'title', width: 80, style: { justifyContent: 'flex-start' } },
        { title: 'URL', dataIndex: 'link', key: 'link', width: 80, style: { justifyContent: 'flex-start' } },
        { title: 'Тематика', dataIndex: 'category', key: 'category', width: 80 },
        { title: 'Подписчики', dataIndex: 'participantsCount', key: 'participantsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Охват 12', dataIndex: 'advPostReach12h', key: 'advPostReach12h', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Охват 24', dataIndex: 'advPostReach24h', key: 'advPostReach24h', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Охват 48', dataIndex: 'advPostReach48h', key: 'advPostReach48h', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Цитируемость', dataIndex: 'ciIndex', key: 'ciIndex', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'ER', dataIndex: 'erPercent', key: 'erPercent', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Показы', dataIndex: 'avgPostReach', key: 'avgPostReach', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Username', dataIndex: 'username', key: 'username', width: 80 },
        { title: 'Страна', dataIndex: 'country', key: 'country', width: 80 },
        { title: 'Язык', dataIndex: 'language', key: 'language', width: 80 },
        { title: 'Тип', dataIndex: 'peerType', key: 'peerType', width: 80 },
        { title: 'Охват дневной', dataIndex: 'dailyReach', key: 'dailyReach', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'ERR', dataIndex: 'errPercent', key: 'errPercent', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'ERR 24', dataIndex: 'err24Percent', key: 'err24Percent', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Репостов канала', dataIndex: 'forwardsCount', key: 'forwardsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Упоминаний каналами', dataIndex: 'mentioningChannelsCount', key: 'mentioningChannelsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Упоминаний всего', dataIndex: 'mentionsCount', key: 'mentionsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Постов', dataIndex: 'postsCount', key: 'postsCount', width: 80, style: { justifyContent: 'flex-end' } },
        { title: 'Platform ID', dataIndex: 'platformId', key: 'platformId', width: 80 },
    ];

    const ColumnsForBuffer: Array<any> = [
        { title: 'Площадка', key: 'title', format: (e: any) => e },
        { title: 'URL', key: 'link', format: (e: any) => e },
        { title: 'Тематика', key: 'category', format: (e: any) => e },
        { title: 'Подписчики', key: 'participantsCount', format: (e: any) => e },
        { title: 'Охват 24', key: 'advPostReach24h', format: (e: any) => e },
        { title: 'Охват 48', key: 'advPostReach48h', format: (e: any) => e },
        { title: 'Цитируемость', key: 'ciIndex', format: (e: any) => String(e).replace('.', ',') },
        { title: 'ER', key: 'erPercent', format: (e: any) => String(e).replace('.', ',') },
    ];

    const ColumnsForBufferAll: Array<any> = [
        { title: 'Статус', key: 'status', format: (e: any) => e },
        { title: 'Платформа', key: 'platform', format: (e: any) => e },
        { title: 'Площадка', key: 'title', format: (e: any) => e },
        { title: 'URL', key: 'link', format: (e: any) => e },
        { title: 'Тематика', key: 'category', format: (e: any) => e },
        { title: 'Подписчики', key: 'participantsCount', format: (e: any) => e },
        { title: 'Охват 12', key: 'advPostReach12h', format: (e: any) => e },
        { title: 'Охват 24', key: 'advPostReach24h', format: (e: any) => e },
        { title: 'Охват 48', key: 'advPostReach48h', format: (e: any) => e },
        { title: 'Цитируемость', key: 'ciIndex', format: (e: any) => String(e).replace('.', ',') },
        { title: 'ER', key: 'erPercent', format: (e: any) => String(e).replace('.', ',') },
        { title: 'Показы', key: 'avgPostReach', format: (e: any) => e },
        { title: 'Username', key: 'username', format: (e: any) => e },
        { title: 'Страна', key: 'country', format: (e: any) => e },
        { title: 'Язык', key: 'language', format: (e: any) => e },
        { title: 'Тип', key: 'peerType', format: (e: any) => e },
        { title: 'Охват дневной', key: 'dailyReach', format: (e: any) => e },
        { title: 'ERR', key: 'errPercent', format: (e: any) => String(e).replace('.', ',') },
        { title: 'ERR 24', key: 'err24Percent', format: (e: any) => String(e).replace('.', ',') },
        { title: 'Репостов канала', key: 'forwardsCount', format: (e: any) => e },
        { title: 'Упоминаний каналами', key: 'mentioningChannelsCount', format: (e: any) => e },
        { title: 'Упоминаний всего', key: 'mentionsCount', format: (e: any) => e },
        { title: 'Постов', key: 'postsCount', format: (e: any) => e },
        { title: 'Platform ID', key: 'platformId', format: (e: any) => e },
    ];

    const ColumnsForBufferPublicsRegister: Array<any> = [
        { title: 'Platfrorm ID', key: 'platformId', format: (e: any) => e },
        ...ColumnsForBuffer,
    ]

    const [status, setStatus] = useState<Status>(Status.isEmpty)
    const [search, setSearch] = useState<Array<string>>(JSON.parse(localStorage.getItem('ExportChannelStats.search') || '[]'))
    const [content, setContent] = useState<Array<any>>([])
    const [searchArrayOpened, setSearchArrayOpened] = useState<Number>(0)

    const { t } = useTranslation();
    const [api, contextHolder] = notification.useNotification();

    const handleSearchArrayOpenedChange = (value: any) => setSearchArrayOpened(Number(value));

    const handleSearchClear = () => {
        localStorage.removeItem('ExportChannelStats.search')
        setSearch([])
    }

    const handleSearchChange = (value: string) => {
        const links = value.split(/[\t\n\s]+/).filter(Boolean);
        const filtered = links.filter(link => {
            const platform = detectPublicLink(link);
            return platform === 'telegram' || platform === 'max';
        });
        localStorage.setItem('ExportChannelStats.search', JSON.stringify(filtered));
        setSearch(filtered);
    };

    const handleSearch = async () => {
        setProgress(0);
        setStatus(Status.isLoading);
        setContent([]);

        const result = [];
        const total = search.length;

        for (const url of search) {
            const platform = detectPublicLink(url);
            if (!platform || (platform !== 'telegram' && platform !== 'max')) {
                result.push({
                    error: true,
                    status: t('ExportChannelStats.channel.unsupportedPlatform'),
                    title: url,
                });
                setProgress((prev) => Math.round(prev + 100 / total));
                continue;
            }

            try {
                const channel = await ChannelDao.getOne(url, platform);
                result.push(channel);
            } catch (error: any) {
                result.push({
                    error: true,
                    status: t('ExportChannelStats.channel.requestError'),
                    title: url,
                    message: error.message,
                });
            }

            setProgress((prev) => Math.round(prev + 100 / total));
        }

        setContent(result);
        setStatus(Status.isContent);
    };

    const getObjectForUser = (channel: IChannel): object => {
        if (!channel.platform_id) return { status: t('ExportChannelStats.channel.isNotFound') }
        // Формируем ссылку для отображения: если уже начинается с http, не добавляем https://
        const linkDisplay = channel.link && !channel.link.startsWith('http') ? `https://${channel.link}` : channel.link;
        
        return {
            status: t('ExportChannelStats.channel.isActive'),
            title: channel.title,
            platform: channel.platform,
            link: linkDisplay,
            category: channel.category,
            participantsCount: channel.participants_count,
            avgPostReach: channel.avg_post_reach,
            username: channel.username,
            country: channel.country,
            language: channel.language,
            peerType: channel.peer_type,
            advPostReach12h: channel.adv_post_reach_12h,
            advPostReach24h: channel.adv_post_reach_24h,
            advPostReach48h: channel.adv_post_reach_48h,
            dailyReach: channel.daily_reach,
            ciIndex: channel.ci_index,
            erPercent: channel.er_percent,
            errPercent: channel.err_percent,
            err24Percent: channel.err24_percent,
            forwardsCount: channel.forwards_count,
            mentioningChannelsCount: channel.mentioning_channels_count,
            mentionsCount: channel.mentions_count,
            postsCount: channel.posts_count,
            platformId: channel.platform_id,
        }
    }

    const copyToClipboardWithPlatformID = async () => {
        try {
            const data = content
                .map(getObjectForUser)
                .map((row: any) => ColumnsForBufferPublicsRegister.map((c: any) => c.format(row[c.key])).join("\t"))
                .join('\n')
            await navigator.clipboard.writeText(data)
            api.success({
                message: t(`ExportChannelStats.copyToClipboard.successMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportChannelStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const copyToClipboardWithHeaders = async () => {
        try {
            const data = [
                columns.map(c => c.title).join("\t"),
                ...content
                    .map(getObjectForUser)
                    .map((row: any) => ColumnsForBufferAll.map((c: any) => c.format(row[c.key])).join("\t"))
            ].join('\n')

            await navigator.clipboard.writeText(data)
            api.success({
                message: t(`ExportChannelStats.copyToClipboard.successMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch (error) {
            console.log(error)
            api.error({
                message: t(`ExportChannelStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const copyToClipboard = async () => {
        try {
            const data = content
                .map(getObjectForUser)
                .map((row: any) => ColumnsForBuffer.map((c: any) => c.format(row[c.key])).join("\t"))
                .join('\n')

            await navigator.clipboard.writeText(data)
            api.success({
                message: t(`ExportChannelStats.copyToClipboard.successMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.successMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        } catch {
            api.error({
                message: t(`ExportChannelStats.copyToClipboard.errorMessage.title`),
                description: t(`ExportChannelStats.copyToClipboard.errorMessage.message`),
                showProgress: false,
                pauseOnHover: false,
                closeIcon: null,
                placement: 'topRight',
            })
        }
    }

    const items: MenuProps['items'] = [
        {
            label: t(`ExportChannelStats.copyToClipboardWithHeaders`),
            key: 'copyToClipboardWithHeaders',
        },
        {
            label: t(`ExportChannelStats.copyToClipboardForPublicsRegister`),
            key: 'copyToClipboardForPublicsRegister',
        },
    ]

    const handleCopyMenuClick = async (e: any) => {
        if (e.key === 'copyToClipboardForPublicsRegister') copyToClipboardWithPlatformID();
        if (e.key === 'copyToClipboardWithHeaders') copyToClipboardWithHeaders();
    };

    return (
        <div style={{ height: '100%', position: 'relative', flexDirection: 'row', }}>
            <Space.Compact style={{ width: '100%' }}>
                <Popover
                    content={
                        <List
                            style={{ maxHeight: '50vh', paddingTop: 5, overflow: "auto" }}
                            itemLayout="horizontal"
                            loading={status === Status.isLoading}
                            dataSource={search}
                            locale={{
                                emptyText: <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t('ExportChannelStats.searchArray.empty')}
                                />
                            }}
                            renderItem={(url, idx) => (
                                <List.Item key={url} style={{ height: 40, marginTop: 10, marginBottom: 0 }}>
                                    <Paragraph
                                        copyable={{
                                            icon: [
                                                <CopyOutlined style={{ color: '#9261f1' }} />,
                                                <CheckOutlined style={{ color: '#9261f1' }} />,
                                            ],
                                        }}
                                        ellipsis={{
                                            expandable: 'collapsible',
                                            symbol: (expanded) => expanded
                                                ? <UpOutlined style={{ color: '#9261f1' }} />
                                                : <DownOutlined style={{ color: '#9261f1' }} />
                                        }}
                                        style={{ left: 0, width: 450 }}
                                        editable={{
                                            icon: <EditOutlined style={{ color: '#9261f1' }} />,
                                            onChange: (value) => handleSearchChange(
                                                search.map((e, i) => i === idx ? value : e).join(' ')
                                            ),
                                        }}
                                    >
                                        {url}
                                    </Paragraph>
                                </List.Item>
                            )}
                        />
                    }
                    placement="bottomRight"
                    title={
                        <div style={{ paddingRight: 10, minWidth: 500 }}>
                            <Tooltip placement="top" title={t('ExportChannelStats.searchArray.titleHelp')}>
                                <Badge count={<QuestionCircleOutlined />} style={{ color: '#9261f1', paddingLeft: 20 }}>
                                    {t('ExportChannelStats.searchArray.title')}
                                </Badge>
                            </Tooltip>
                        </div>
                    }
                    trigger="click"
                    open={!!searchArrayOpened}
                    onOpenChange={handleSearchArrayOpenedChange}>
                    <Badge count={search.length} color="#9261f1" overflowCount={99} style={{ zIndex: 1 }}>
                        <Avatar
                            style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                background: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRight: 0,
                                color: '#000',
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                            shape="square"
                            size="large">
                            {!!searchArrayOpened ? (
                                <UpOutlined />
                            ) : (
                                <DownOutlined />
                            )}
                        </Avatar>
                    </Badge>
                </Popover>
                <Input placeholder={t(`ExportChannelStats.search.placeholder`)}
                    size="large"
                    value={search.join(', ')}
                    onPressEnter={handleSearch}
                    onChange={e => handleSearchChange(e.target.value)}
                    onFocus={e => e.target.select()}
                    disabled={Boolean(search.length)}
                />
                <Button
                    size="large"
                    onClick={handleSearchClear}>
                    <CloseOutlined />
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSearch}
                    loading={status === Status.isLoading}>
                    {t(`ExportChannelStats.search.button`)}
                </Button>
            </Space.Compact>
            <UsersManualExportButton t={t} />
            {status === Status.isEmpty && (
                <Result style={{ paddingTop: '25vh', opacity: .7 }}
                    icon={<Empty description={false} />}
                    status="warning"
                    title={t(`ExportChannelStats.search.empty`)}
                />
            )}
            {status === Status.isContent && (
                <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', height: 'calc(100% - 70px)' }}>
                    <Space align="end" direction="vertical">
                        <Dropdown.Button
                            menu={{
                                items,
                                onClick: handleCopyMenuClick,
                            }}
                            icon={<DownOutlined />}
                            type="text"
                            placement="bottom"
                            onClick={copyToClipboard}
                            style={{ color: "#e7e7e7", borderTopLeftRadius: 6, borderTopRightRadius: 6, background: '#efefef', alignSelf: 'flex-end' }}
                        >
                            {t(`ExportChannelStats.copyToClipboard.action`)}
                        </Dropdown.Button>
                    </Space>
                    <BaseGrid
                        columnsDesc={columns}
                        style={{ height: '100%' }}
                        data={
                            content.map((e) => ({ key: uniqid(), ...getObjectForUser(e) }))
                        }
                        onEdit={(): void => { }}
                    />
                </div>
            )}
            {status === Status.isLoading && (
                <SpinLoading progress={progress} />
            )}
            {contextHolder}
        </div>
    );
}

export default ExportChannelStats